/**
 * Pure arithmetic formula evaluator for calculation variables.
 *
 * This module is dependency-free (no React, no store, no DOM) so it can be
 * copied verbatim into the static-library runtime repo. Do not import app
 * modules from here.
 *
 * Grammar (v1, no parentheses):
 *   operand   := NUMBER | VARIABLE
 *   NUMBER    := an optional leading "-" followed by digits, optionally
 *                followed by "." and more digits (e.g. "3", "-3", "2.5")
 *   VARIABLE  := "{{" name "}}" where name may be dotted (e.g. "a.b.c")
 *   term      := operand (("*" | "x" | "X" | "×" | "/" | "÷") operand)*
 *   expr      := term (("+" | "-") term)*
 *
 * "x"/"X"/"×" normalize to multiply, "÷" normalizes to divide.
 * "*"/"/"/"×"/"÷" bind tighter than "+"/"-"; same-precedence operators are
 * left-associative. Whitespace between tokens is ignored. Parentheses are
 * not supported in v1 and produce a parse error.
 */

/** A bare arithmetic operator, after normalizing "x"/"×"/"÷" aliases. */
export type FormulaOperator = "+" | "-" | "*" | "/";

type Token =
  | { type: "num"; value: number }
  | { type: "var"; name: string }
  | { type: "op"; op: FormulaOperator };

/** Evaluable AST node produced by {@link parseFormula}. */
export type FormulaAst =
  | { type: "num"; value: number }
  | { type: "var"; name: string }
  | { type: "binop"; op: FormulaOperator; left: FormulaAst; right: FormulaAst };

export type ParseFormulaResult =
  | { ok: true; ast: FormulaAst }
  | { ok: false; error: string };

export interface EvaluateFormulaResult {
  value: number | null;
  error?: string;
}

// Matches a `{{name}}` (or dotted `{{a.b.c}}`) reference, with optional
// whitespace hugging the name inside the braces.
const VAR_RE = /^\{\{\s*([\w.]+)\s*\}\}/;

// Matches an unsigned integer or decimal literal (the leading "-" for a
// negative literal is handled separately by the tokenizer, since it must be
// distinguished from the binary subtract operator).
const NUM_RE = /^\d+(?:\.\d+)?/;

const OPERATOR_CHARS: Record<string, FormulaOperator> = {
  "+": "+",
  "-": "-",
  "*": "*",
  "/": "/",
  x: "*",
  X: "*",
  "×": "*",
  "÷": "/",
};

class FormulaParseError extends Error {}

const isDigitOrDot = (ch: string | undefined): boolean =>
  ch !== undefined && (ch >= "0" && ch <= "9" || ch === ".");

/**
 * Splits a formula string into a flat token stream. A leading "-" is folded
 * into a numeric literal (making it negative) whenever it appears where an
 * operand is expected — i.e. at the very start of the formula, or right
 * after another operator — otherwise it is treated as the binary subtract
 * operator.
 */
const tokenize = (
  formula: string,
): { ok: true; tokens: Token[] } | { ok: false; error: string } => {
  const tokens: Token[] = [];
  let i = 0;
  const n = formula.length;

  while (i < n) {
    const ch = formula[i];

    if (ch === " " || ch === "\t" || ch === "\n" || ch === "\r") {
      i += 1;
      continue;
    }

    if (ch === "(" || ch === ")") {
      return {
        ok: false,
        error: `parentheses are not supported (at position ${i})`,
      };
    }

    if (ch === "{" && formula[i + 1] === "{") {
      const match = VAR_RE.exec(formula.slice(i));
      if (!match) {
        return {
          ok: false,
          error: `invalid or unterminated variable reference (at position ${i})`,
        };
      }
      tokens.push({ type: "var", name: match[1] });
      i += match[0].length;
      continue;
    }

    const atOperandPosition =
      tokens.length === 0 || tokens[tokens.length - 1].type === "op";

    if (ch === "-" && atOperandPosition && isDigitOrDot(formula[i + 1])) {
      const match = NUM_RE.exec(formula.slice(i + 1));
      if (!match) {
        return {
          ok: false,
          error: `invalid number literal (at position ${i})`,
        };
      }
      tokens.push({ type: "num", value: -Number(match[0]) });
      i += 1 + match[0].length;
      continue;
    }

    if (ch >= "0" && ch <= "9") {
      const match = NUM_RE.exec(formula.slice(i));
      if (!match) {
        return {
          ok: false,
          error: `invalid number literal (at position ${i})`,
        };
      }
      tokens.push({ type: "num", value: Number(match[0]) });
      i += match[0].length;
      continue;
    }

    const op = OPERATOR_CHARS[ch];
    if (op) {
      tokens.push({ type: "op", op });
      i += 1;
      continue;
    }

    return {
      ok: false,
      error: `unexpected character '${ch}' (at position ${i})`,
    };
  }

  return { ok: true, tokens };
};

/** Recursive-descent parse of a flat token stream into a {@link FormulaAst}. */
const parseTokens = (tokens: Token[]): FormulaAst => {
  let pos = 0;
  const peek = () => tokens[pos];
  const advance = () => tokens[pos++];

  const parseOperand = (): FormulaAst => {
    const tok = peek();
    if (!tok) {
      throw new FormulaParseError(
        "unexpected end of formula, expected a number or variable",
      );
    }
    if (tok.type === "op") {
      throw new FormulaParseError(
        `unexpected operator '${tok.op}', expected a number or variable`,
      );
    }
    advance();
    return tok.type === "num"
      ? { type: "num", value: tok.value }
      : { type: "var", name: tok.name };
  };

  const parseTerm = (): FormulaAst => {
    let left = parseOperand();
    for (;;) {
      const tok = peek();
      if (tok?.type === "op" && (tok.op === "*" || tok.op === "/")) {
        advance();
        const right = parseOperand();
        left = { type: "binop", op: tok.op, left, right };
      } else {
        break;
      }
    }
    return left;
  };

  const parseExpression = (): FormulaAst => {
    let left = parseTerm();
    for (;;) {
      const tok = peek();
      if (tok?.type === "op" && (tok.op === "+" || tok.op === "-")) {
        advance();
        const right = parseTerm();
        left = { type: "binop", op: tok.op, left, right };
      } else {
        break;
      }
    }
    return left;
  };

  const ast = parseExpression();
  const trailing = peek();
  if (trailing) {
    const near =
      trailing.type === "op"
        ? `'${trailing.op}'`
        : trailing.type === "num"
          ? `'${trailing.value}'`
          : `'{{${trailing.name}}}'`;
    throw new FormulaParseError(`unexpected token after formula end (near ${near})`);
  }

  return ast;
};

/**
 * Returns the distinct `{{name}}` variable names referenced by a formula, in
 * first-seen order. Numeric literals are never included. Works directly off
 * the raw string (independent of parsing) so it still reports operands for a
 * formula that otherwise fails to parse.
 */
export const getFormulaTokens = (formula: string): string[] => {
  const re = /\{\{\s*([\w.]+)\s*\}\}/g;
  const seen = new Set<string>();
  let match: RegExpExecArray | null = re.exec(formula);
  while (match) {
    seen.add(match[1]);
    match = re.exec(formula);
  }
  return Array.from(seen);
};

/**
 * Tokenizes and parses a formula into an evaluable AST. Returns a parse
 * error (never throws) for empty input, unknown characters, parentheses, or
 * a malformed operator/operand sequence (e.g. a trailing operator).
 */
export const parseFormula = (formula: string): ParseFormulaResult => {
  const tokenized = tokenize(formula);
  if (!tokenized.ok) {
    return { ok: false, error: tokenized.error };
  }
  if (tokenized.tokens.length === 0) {
    return { ok: false, error: "empty formula" };
  }
  try {
    const ast = parseTokens(tokenized.tokens);
    return { ok: true, ast };
  } catch (e) {
    if (e instanceof FormulaParseError) {
      return { ok: false, error: e.message };
    }
    throw e;
  }
};

/**
 * Parses and evaluates a formula, resolving each `{{token}}` via `resolve`.
 *
 * - A genuine parse error (bad syntax, parentheses, trailing operator, …)
 *   sets `error` and returns `value: null`.
 * - A missing/non-numeric operand (`resolve` returns `undefined` or a
 *   non-finite number for any referenced token) returns `value: null` with
 *   NO `error` — the caller is expected to fall back to the variable's own
 *   display/blank, not to show a formula error.
 * - Division by zero returns `value: null` with `error: "division by zero"`.
 * - Any other non-finite result (NaN/Infinity) is also mapped to
 *   `value: null` with no error, rather than leaking out.
 */
export const evaluateFormula = (
  formula: string,
  resolve: (token: string) => number | undefined,
): EvaluateFormulaResult => {
  const parsed = parseFormula(formula);
  if (!parsed.ok) {
    return { value: null, error: parsed.error };
  }

  let missingOperand = false;
  let divisionByZero = false;

  const evalNode = (node: FormulaAst): number => {
    if (missingOperand || divisionByZero) {
      return 0;
    }

    if (node.type === "num") {
      return node.value;
    }

    if (node.type === "var") {
      const resolved = resolve(node.name);
      if (resolved === undefined || !Number.isFinite(resolved)) {
        missingOperand = true;
        return 0;
      }
      return resolved;
    }

    const left = evalNode(node.left);
    const right = evalNode(node.right);
    if (missingOperand || divisionByZero) {
      return 0;
    }

    switch (node.op) {
      case "+":
        return left + right;
      case "-":
        return left - right;
      case "*":
        return left * right;
      case "/":
        if (right === 0) {
          divisionByZero = true;
          return 0;
        }
        return left / right;
      default:
        return 0;
    }
  };

  const result = evalNode(parsed.ast);

  if (divisionByZero) {
    return { value: null, error: "division by zero" };
  }
  if (missingOperand) {
    return { value: null };
  }
  if (!Number.isFinite(result)) {
    return { value: null };
  }

  return { value: result };
};
